import { HTMLAttributes, useRef } from 'react';
import { stopAddEvent } from '../../modules/analytics';

// Constants
import { colors } from '../../constants/colors';

// Interfaces
import { Stop as StopI } from '../../interfaces';

// Utils
import { reorderArray } from '../../utils/reorder-array';

// Components
import Stop from './stop/stop';

import './stops.scss';

interface StopsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
	value?: Array<any>;
	onChange?: (value: Array<any>) => void;
}

const Stops: React.FunctionComponent<StopsProps> = ({ value = [], onChange, ...props }) => {
	const stepsRefs = useRef<Array<HTMLDivElement | null>>([]);

	function getStopColor(index: number) {
		const isLastStop = value.length === index + 1;
		const lastColor = colors[colors.length - 1];
		const current = colors[index % colors.length];
		const previous = colors[(index - 1) % colors.length];
		const next = colors[(index + 1) % colors.length];

		return {
			previous,
			current: isLastStop ? lastColor : current,
			next: isLastStop ? null : next
		};
	}

	function onStopRemove(index: number) {
		const nextValue = [...value];

		nextValue.splice(index, 1);

		onChange?.(nextValue);
	}

	function onStopChange(index: number, step: StopI) {
		const nextValue = [...value];

		nextValue[index] = step;

		if (!value[index]) {
			stopAddEvent();
		}

		onChange?.(nextValue);
	}

	function onDragEnd(index: number, position: number) {
		let nextIndex = stepsRefs.current.length - 1;

		stepsRefs.current.forEach((stepEl, idx) => {
			if (idx === index) return;

			const currentPosition = position + (stepsRefs.current[index]?.offsetTop ?? 0);
			const currentStepPosition = stepEl?.offsetTop ?? 0;

			if (currentPosition < currentStepPosition && nextIndex > idx) {
				nextIndex = idx;
			}
		});

		if (nextIndex !== index) {
			onChange?.(reorderArray(value, index, nextIndex));
		}
	}

	return (
		<div className="stops" {...props}>
			{value.map((stop, index) => {
				const color = getStopColor(index);

				return (
					<div
						className="stops__step-wrapper"
						ref={ref => (stepsRefs.current[index] = ref)}
						key={`step-${Math.random()}`}
					>
						{index ? (
							<div
								className="stops__step-wrapper__line-before"
								style={{ background: `linear-gradient(${color.previous}, ${color.current})` }}
							/>
						) : null}
						<Stop
							stop={stop}
							color={color}
							onRemove={() => onStopRemove(index)}
							onChange={stop => onStopChange(index, stop)}
							onDragEnd={position => onDragEnd(index, position)}
						/>
					</div>
				);
			})}

			<Stop
				key={`step-input-${value.length}`}
				stop={{}}
				draggable={false}
				removable={false}
				autoFocusInput
				color={{ current: colors[value.length] }}
				placeholder={!value.length ? 'Enter starting address' : 'Enter destination address'}
				onChange={step => onStopChange(value.length, step)}
			/>
		</div>
	);
};

export default Stops;
